import { Component, Input, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA, inject 
} from '@angular/core';
import { Router } from '@angular/router';
import { MaterialSharedModule } from '@app/shared/modules/material-shared.module';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BaseSharedModule } from '@app/shared/modules/base-shared.module';
import { LoggerService } from '@app/core/infrastructure';
import { TranslateService } from '@ngx-translate/core';
import { AuthFacade } from '@app/store/auth/auth.facade';
import { VersionService } from '@app/core/infrastructure';
import { environment } from '../../../environments/environment';
import { Language } from '@app/shared/models';
import { LanguageFacade } from '@app/store/language';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { UserFacade } from '@app/store/user/user.facade';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserResponseSchema } from '@app/core/api/user';
import { SharedModule } from '@app/shared/modules/shared.module';

@UntilDestroy()
@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: true,
  imports: [
    BaseSharedModule,
    MaterialSharedModule,
    AngularSvgIconModule,
    SharedModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SidenavComponent {
  private readonly logger = inject(LoggerService).scope('SidenavComponent');
  
  @Input() isOpen = false;
  @Output() toggleSidenav = new EventEmitter<void>();
  @Output() closeSidenav = new EventEmitter<void>();

  version: string = '';
  currentLanguage: Language = Language.EN;
  currentLanguageFlag: string = '\u{1F1FA}\u{1F1F8}';
  currentLanguageLabel: string = 'English';
  userName$: Observable<string>;
  private translate = inject(TranslateService);
  languageOptions: any[] = [];

  constructor(
    private router: Router,
    private authFacade: AuthFacade,
    private versionService: VersionService,
    private languageFacade: LanguageFacade,
    private bottomSheet: MatBottomSheet,
    private userFacade: UserFacade,
  ) {
    this.version = this.versionService.getVersion();
    
    this.updateLanguageOptions();
    
    this.translate.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateLanguageOptions();
        this.updateLanguageDisplay();
      });
    
    this.languageFacade.activeLanguage$
      .pipe(untilDestroyed(this))
      .subscribe((language: Language) => {
        this.currentLanguage = language;
        this.updateLanguageDisplay();
      });

    this.userName$ = this.userFacade.profile$.pipe(
      map((profile: UserResponseSchema | null) => {
        return profile?.first_name || profile?.name || '';
      })
    );
  }

  private updateLanguageOptions(): void {
    const options = this.languageFacade.getLanguageOptions();
    this.languageOptions = options.map(opt => ({
      ...opt,
      label: this.translate.instant(opt.label)
    }));
  }

  private updateLanguageDisplay(): void {
    const languageOption = this.languageOptions.find(opt => opt.value === this.currentLanguage);
    if (languageOption) {
      this.currentLanguageFlag = languageOption.flag;
      this.currentLanguageLabel = languageOption.label;
    }
  }

  openLanguageSelector(): void {
    this.bottomSheet.open(LanguageSelectorComponent, {
      ariaLabel: 'Select language'
    });
  }

  logout(): void {
    this.authFacade.logout();
    this.logger.debug('Logout dispatched');
    this.closeSidenav.emit();
  }

  onToggle(): void {
    this.toggleSidenav.emit();
  }

  onBackdropClick(): void {
    this.closeSidenav.emit();
  }

  showDebugInfo(): void {
    const envName = this.getEnvironmentName();
    const debugInfo = {
      version: this.version,
      environment: envName,
      apiUrl: environment.API_BASE_URL,
      platform: this.getPlatformInfo(),
      currentPath: this.router.url,
      logLevel: environment.logLevel,
      timestamp: new Date().toISOString()
    };
    
    const debugText = `
DEBUG INFO:
Version: ${debugInfo.version}
Environment: ${debugInfo.environment}
API URL: ${debugInfo.apiUrl}
Platform: ${debugInfo.platform}
Log Level: ${debugInfo.logLevel}
Current Route: ${debugInfo.currentPath}
Timestamp: ${debugInfo.timestamp}
    `.trim();

    this.logger.debug('DEBUG INFO (Full):', {
      ...debugInfo,
      userAgent: navigator.userAgent,
      currentUrl: window.location.href,
      production: environment.production,
    });

    alert(debugText);
  }

  private getPlatformInfo(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('android')) return 'Android';
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) return 'iOS';
    if (userAgent.includes('mobile')) return 'Mobile Web';
    return 'Desktop Web';
  }

  private getEnvironmentName(): string {
    const apiUrl = environment.API_BASE_URL;
    if (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1') || apiUrl.includes('trycloudflare.com')) {
      return 'Development';
    } else if (apiUrl.includes('-stg.') || apiUrl.includes('staging')) {
      return 'Staging';
    } else if (environment.production) {
      return 'Production';
    }
    return 'Unknown';
  }
}
